import { APPLY_UPDATE_CURRENT_TAB } from './serviceWorkerProtocol';

const DEFAULT_ACTIVATION_TIMEOUT_MS = 15 * 1000;
let pendingUpdate = null;

export function getPendingServiceWorkerUpdate() {
  if (pendingUpdate?.worker?.state === 'redundant' && !pendingUpdate.registration?.waiting && !pendingUpdate.registration?.installing) pendingUpdate = null;
  return pendingUpdate;
}

/**
 * Return an explicit update action for this tab. A controller can change in
 * every open tab when one tab activates a waiting worker, so controllerchange
 * alone must never reload the page.
 */
export function registerServiceWorkerControllerRefresh({
  serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  reload = () => window.location.reload(),
  onRefresh = () => {},
  activationTimeoutMs = DEFAULT_ACTIVATION_TIMEOUT_MS,
} = {}) {
  if (!serviceWorker) {
    return () => Promise.reject(new Error('Service worker updates are unavailable.'));
  }

  let activeRequest = null;
  const clearRequest = () => {
    const request = activeRequest;
    if (!request) return null;
    activeRequest = null;
    window.clearTimeout(request.timeoutId);
    request.registration?.removeEventListener?.('updatefound', request.advance);
    for (const worker of request.observed) worker.removeEventListener?.('statechange', request.advance);
    return request;
  };
  const finishRefresh = () => {
    const request = clearRequest();
    if (!request) return;
    onRefresh(); request.resolve(true); reload();
  };
  serviceWorker.addEventListener('controllerchange', () => activeRequest?.advance());

  return (worker, registration) => {
    if (activeRequest) return activeRequest.promise;
    if (!worker && !registration?.waiting && !registration?.installing) {
      return Promise.reject(new Error('The update is no longer available.'));
    }
    let resolveRequest;
    let rejectRequest;
    const promise = new Promise((resolve, reject) => { resolveRequest = resolve; rejectRequest = reject; });
    const request = { worker, registration, promise, resolve: resolveRequest, reject: rejectRequest,
      observed: new Set(), messaged: new Set() };
    request.advance = () => {
      if (activeRequest !== request) return;
      // Resolve the newest worker at click time and again at every lifecycle
      // event. A banner may refer to a worker since replaced by another deploy.
      const newest = [registration?.installing, registration?.waiting].find(candidate => candidate && candidate.state !== 'redundant');
      if (newest) request.worker = newest;
      else if (request.worker?.state === 'redundant' && registration?.active) request.worker = registration.active;
      const target = request.worker;
      if (!target || target.state === 'redundant') return;
      if (!request.observed.has(target)) {
        request.observed.add(target);
        target.addEventListener?.('statechange', request.advance);
      }
      // Activation alone is too early: navigation may still be served by the
      // old controller. Only the requesting tab reloads, after takeover.
      if (serviceWorker.controller === target) { finishRefresh(); return; }
      if (target.state === 'installing') return;
      if (!request.messaged.has(target)) {
        request.messaged.add(target);
        try { target.postMessage({ type: APPLY_UPDATE_CURRENT_TAB }); }
        catch (error) { clearRequest()?.reject(error); }
      }
    };
    request.timeoutId = window.setTimeout(() => {
      clearRequest()?.reject(new Error('The update is taking too long. Please try again.'));
    }, activationTimeoutMs);
    activeRequest = request;
    registration?.addEventListener?.('updatefound', request.advance);
    request.advance();
    return promise;
  };
}

export function announceServiceWorkerUpdate({
  registration,
  installedWorker,
  isPublicStandalone = false,
  applyServiceWorkerUpdate,
  windowObject = typeof window !== 'undefined' ? window : null,
} = {}) {
  if (!windowObject || !applyServiceWorkerUpdate) return false;

  const workerToActivate = installedWorker || registration?.waiting;
  if (!workerToActivate) return false;

  pendingUpdate = {
    worker: workerToActivate,
    registration,
    applyUpdate: () => applyServiceWorkerUpdate(registration?.installing || registration?.waiting || workerToActivate, registration),
  };

  // Preserve discovery during login/public routes for the authenticated shell.
  if (isPublicStandalone) return false;
  windowObject.dispatchEvent(
    new CustomEvent('vela:sw-update-available', {
      detail: pendingUpdate,
    })
  );
  return true;
}
